<template>
  <BField
    :label="label"
    :type="{ 'is-error': getValidation.invalid }"
    :message="getValidation.message"
  >
    <BInput
      v-model="localValue"
      v-validate="getValidationRule"
      :placeholder="getPlaceholder"
      test-id="ls-input"
      :disabled="disabled || loading"
      :name="name"
      :icon="getIcon"
      :loading="loading"
      :readonly="readonly"
      type="tel"
      maxlength="50"
      :has-counter="false"
      @blur="onBlur"
      @input.native="onInput"
    ></BInput>
  </BField>
</template>

<script>
import VueTypes from 'vue-types';
import {
  lsPropTypeHelpers,
  lsTrackingService,
  lsValidationService,
  lsValidations,
} from 'ls-vue-common';
import Cleave from 'cleave.js';
import _ from 'lodash';

require('cleave.js/dist/addons/cleave-phone.us');

export default {
  name: 'LsInputMasked',
  inject: ['$validator'],
  props: {
    value: VueTypes.string.isRequired,
    trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
    mask: VueTypes.oneOf(['creditCard', 'phone', 'currency']).isRequired,
    name: VueTypes.string.isRequired,
    label: VueTypes.string,
    placeholder: VueTypes.string,
    disabled: VueTypes.bool.def(false),
    validation: VueTypes.object.def(_.constant(null)),
    loading: VueTypes.bool.def(false),
    readonly: VueTypes.bool.def(false),
  },
  data() {
    return {
      cleave: null,
      localValue: this.value,
    };
  },
  computed: {
    getPlaceholder() {
      const placeholders = {
        creditCard: 'e.g. 1234 5678 9012 3456',
        currency: 'e.g. 45.99',
      };

      return this.placeholder || placeholders[this.mask];
    },
    getValidationRule() {
      return this.validation || lsValidations[this.mask];
    },
    getIcon() {
      if (this.mask !== 'currency') {
        return;
      }

      return this.getValidation.invalid ? 'replaceCurrencyError' : 'replaceCurrency';
    },
    getMask() {
      const masks = {
        creditCard: { creditCard: true },
        phone: {
          phone: true,
          phoneRegionCode: 'US',
        },
        currency: {
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralPositiveOnly: true,
          numeralIntegerScale: 6, // maxes out at 999,999.99
        },
      };

      return masks[this.mask];
    },
    getValidation() {
      return lsValidationService.getValidation.call(this);
    },
  },
  mounted() {
    const el = this.$el.querySelector('input');
    this.cleave = new Cleave(el, this.getMask);
    this.cleave.setRawValue(this.value);
    this.localValue = this.cleave.getFormattedValue();
  },
  beforeDestroy() {
    this.cleave.destroy();
    this.cleave = null;
  },
  methods: {
    onBlur() {
      lsTrackingService.track({
        event: 'blur',
        target: this.trackId,
      });

      this.$emit('blur', this.cleave.getRawValue());
    },

    onInput() {
      // setTimout fixes weird issue on Android where user can enter in one extra character
      setTimeout(() => {
        this.localValue = this.cleave.getFormattedValue();
        this.$emit('input', this.cleave.getRawValue());
      });
    },
  },
};
</script>

<style lang="scss">
.mdi-replaceCurrency::after {
  content: '$';
  color: $white;
  position: absolute;
  background-color: $primary;
  top: 1px;
  padding: 5.25px 8px;
  right: 5.5px;
  font-style: normal;
  font-weight: bold;
}

.mdi-replaceCurrencyError::after {
  content: '$';
  color: $white;
  position: absolute;
  background-color: $danger;
  top: 1px;
  padding: 5.25px 8px;
  right: 5.5px;
  font-style: normal;
  font-weight: bold;
}

.themed-app {
  .mdi-replaceCurrency::after {
    background-color: $theme-color-primary;
    color: $theme-color-white;
  }

  .mdi-replaceCurrencyError::after {
    background-color: $theme-color-danger;
    color: $theme-color-white;
  }
}
</style>
